import toast from "react-hot-toast";
import {
  MessageSquare,
  Plus,
  Edit2,
  Trash2,
  Star,
  Search,
  Tag,
  Type,
  Copy,
  Eye,
  Download,
} from "lucide-react";
import { useState } from "react";
import ConfirmModal from "../modal/ConfirmModal";
import QuickMessageModal from "../modal/QuickMessageModal";
import { useQuickMessages } from "../hooks/useQuickMessages";

const MESSAGE_TYPE_ICONS = {
  text: "📝",
  image: "🖼️",
  document: "📄",
  audio: "🎵",
  video: "🎥",
};

const QuickMessages = () => {
  const {
    quickMessages,
    addQuickMessage,
    updateQuickMessage,
    deleteQuickMessage,
    loading,
    error,
  } = useQuickMessages();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  const CATEGORIES = [
    "Geral",
    "Saudação",
    "Despedida",
    "Vendas",
    "Suporte",
    "Agradecimento",
    "Confirmação",
    "Promoção",
  ];

  const MESSAGE_TYPES = [
    { value: "text", label: "Texto" },
    { value: "image", label: "Imagem" },
    { value: "document", label: "Documento" },
    { value: "audio", label: "Áudio" },
    { value: "video", label: "Vídeo" },
  ];

  const filteredMessages = quickMessages.filter((message) => {
    const term = searchTerm.toLowerCase();
    const titleMatch = message.title?.toLowerCase().includes(term);
    const contentMatch = message.content?.toLowerCase().includes(term);
    const tagsMatch = message.tags?.some((tag) =>
      tag.toLowerCase().includes(term),
    );

    const categoryMatch =
      filterCategory === "all" || message.category === filterCategory;
    const typeMatch = filterType === "all" || message.type === filterType;
    const favoriteMatch = !filterFavorite || message.isFavorite === true;

    return (
      (titleMatch || contentMatch || tagsMatch) &&
      categoryMatch &&
      typeMatch &&
      favoriteMatch
    );
  });

  const openMessageModal = () => {
    setSelectedMessage(null);
    setIsModalOpen(true);
  };

  const handleSaveMessage = async (messageData) => {
    try {
      if (selectedMessage) {
        await updateQuickMessage(selectedMessage.id, messageData);
        toast.success("Mensagem atualizada com sucesso!");
      } else {
        await addQuickMessage(messageData);
        toast.success("Mensagem criada com sucesso!");
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao salvar a mensagem.");
    }
  };

  const handleEditMessage = (message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const handleDeleteMessage = (messageId) => {
    setMessageToDelete(messageId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (messageToDelete) {
        await deleteQuickMessage(messageToDelete);
        toast.success("Mensagem excluída com sucesso!");
        setMessageToDelete(null);
        setConfirmOpen(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir a mensagem.");
    }
  };

  const handleCopyContent = (content) => {
    navigator.clipboard.writeText(content);
    toast.success("Conteúdo copiado!");
  };

  if (loading)
    return (
      <p className="p-6 text-gray-700 dark:text-gray-300">
        Carregando mensagens...
      </p>
    );
  if (error) return <p className="text-red-500">Erro: {error}</p>;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        {/* Cabeçalho */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Mensagens Rápidas
          </h1>
          <button
            onClick={openMessageModal}
            className="flex items-center rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova Mensagem
          </button>
        </div>

        {/* Estatísticas */}
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <MessageSquare className="mx-auto mb-2 h-8 w-8 text-blue-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {quickMessages.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total de Mensagens
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <Star className="mx-auto mb-2 h-8 w-8 text-yellow-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {quickMessages.filter((m) => m.isFavorite).length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Favoritas
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <Tag className="mx-auto mb-2 h-8 w-8 text-green-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {new Set(CATEGORIES.map((cat) => cat)).size}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Categorias
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <Type className="mx-auto mb-2 h-8 w-8 text-purple-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {MESSAGE_TYPES.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Tipos de Mensagem
            </p>
          </div>
        </div>

        {/* Filtros e Busca */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {/* Busca */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar mensagens..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>

            {/* Filtro de Categoria */}
            <div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="all">Todas as Categorias</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro de Tipo */}
            <div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="all">Todos os Tipos</option>
                {MESSAGE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtro de Favoritos */}
          <div className="mt-3 flex items-center space-x-2">
            <input
              type="checkbox"
              id="filterFavorite"
              checked={filterFavorite}
              onChange={(e) => setFilterFavorite(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500 dark:border-gray-600 dark:bg-gray-800"
            />
            <label
              htmlFor="filterFavorite"
              className="flex cursor-pointer items-center text-sm text-gray-700 dark:text-gray-300"
            >
              <Star className="mr-1 h-4 w-4 text-yellow-500" />
              Mostrar apenas favoritas
            </label>
          </div>
        </div>

        {/* Grid de Mensagens */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredMessages.map((message) => (
            <div
              key={message.id}
              className="group relative rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition-all hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
            >
              {/* Header do Card */}
              <div className="mb-3 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {message.title}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Conteúdo */}
              <p className="mb-4 line-clamp-3 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-400">
                {message.content}
              </p>

              {/* Tags e Categoria */}
              <div className="mb-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  {message.category.charAt(0).toUpperCase() +
                    message.category.slice(1)}{" "}
                </span>
                <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                  {MESSAGE_TYPE_ICONS[message.type]}{" "}
                  {MESSAGE_TYPES.find((t) => t.value === message.type)?.label}
                </span>
                {message.tags?.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Ações */}
              <div className="flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
                <button
                  onClick={() => handleCopyContent(message.content)}
                  className="flex items-center space-x-1 rounded p-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-green-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-green-400"
                  title="Copiar conteúdo"
                >
                  <Copy className="h-4 w-4" />
                  <span className="text-xs">Copiar</span>
                </button>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleEditMessage(message)}
                    className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                    title="Editar"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteMessage(message.id)}
                    className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    title="Excluir"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Botões de Anexo */}
              <div className="mt-3 flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {message.attachmentUrl
                    ? "Arquivo Anexado"
                    : "Mensagem sem Anexo"}
                </span>
                {message.attachmentUrl && (
                  <div className="flex space-x-2">
                    {/* Visualizar */}
                    <a
                      href={`${process.env.REACT_APP_URL}${message.attachmentUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40"
                      title="Visualizar arquivo"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Visualizar</span>
                    </a>

                    {/* Download */}
                    <a
                      href={`${process.env.REACT_APP_URL}${message.attachmentUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={message.attachmentUrl}
                      className="flex items-center space-x-1 rounded bg-green-50 px-2 py-1 text-xs font-medium text-green-600 transition-colors hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40"
                      title="Baixar arquivo"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}

          {filteredMessages.length === 0 && (
            <div className="col-span-full rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-900">
              <MessageSquare className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <p className="text-gray-500 dark:text-gray-400">
                Nenhuma mensagem encontrada.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modais */}
      <QuickMessageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMessage}
        quickMessage={selectedMessage}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Excluir Mensagem"
        message="Tem certeza que deseja excluir esta mensagem? Esta ação não pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default QuickMessages;
